import React, { Component } from "react";
import { AlertTriangle, RefreshCw, Home, Bug } from "lucide-react";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
      errorInfo: null,
      showDetails: false,
    };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    if (process.env.NODE_ENV === "development") {
      console.error("Error Boundary caught an error:", error, errorInfo);
    }
  }

  handleRefresh = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  toggleDetails = () => {
    this.setState((prevState) => ({
      showDetails: !prevState.showDetails,
    }));
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="dark min-h-screen bg-background text-foreground flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-card border border-border rounded-xl p-6 shadow-lg">
            {/* Icon */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-brand/20 rounded-full mb-4 animate-pulse">
                <AlertTriangle className="w-10 h-10 text-brand" />
              </div>
              <h1 className="text-3xl font-bold mb-2">Something went wrong</h1>
              <p className="text-muted-foreground text-sm">
                An unexpected error occurred. We’ve been notified and are
                working to resolve the issue.
              </p>
            </div>

            {/* Buttons */}
            <div className="space-y-3 mb-6">
              <button
                onClick={this.handleRefresh}
                className="w-full flex items-center justify-center gap-2 bg-brand text-black font-medium py-3 px-4 rounded-lg transition-transform shadow hover:shadow-lg"
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </button>

              <button
                onClick={this.handleGoHome}
                className="w-full flex items-center justify-center gap-2 border border-border text-foreground font-medium py-3 px-4 rounded-lg transition-transform hover:bg-muted"
              >
                <Home className="w-4 h-4" />
                Go to Homepage
              </button>
            </div>

            {/* Technical Details */}
            {(this.state.error || this.state.errorInfo) && (
              <div className="border-t border-border pt-6">
                <button
                  onClick={this.toggleDetails}
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
                >
                  <Bug className="w-4 h-4" />
                  {this.state.showDetails ? "Hide" : "Show"} Technical Details
                </button>

                {this.state.showDetails && (
                  <div className="bg-card rounded-lg p-4 border border-border animate-in slide-in-from-top-2 duration-200">
                    <div className="space-y-4">
                      {this.state.error && (
                        <div>
                          <h3 className="font-medium text-foreground mb-2">
                            Error Message:
                          </h3>
                          <pre className="text-xs bg-red-950 text-red-200 p-3 rounded border border-red-400 overflow-x-auto whitespace-pre-wrap">
                            {this.state.error.toString()}
                          </pre>
                        </div>
                      )}
                      {this.state.errorInfo && (
                        <div>
                          <h3 className="font-medium text-foreground mb-2">
                            Component Stack:
                          </h3>
                          <pre className="text-xs bg-neutral-900 text-muted-foreground p-3 rounded border border-neutral-700 overflow-x-auto whitespace-pre-wrap max-h-40 overflow-y-auto">
                            {this.state.errorInfo.componentStack}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Error ID Footer */}
            <div className="text-center mt-6 text-xs text-muted-foreground">
              Error ID: {Date.now().toString(36).toUpperCase()}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
